// import React, { useState, useEffect } from 'react';
import { useState, useEffect } from 'react';
import { AdminCollectionInfo } from '../../types'; 
import { useNotification } from '../../contexts/NotificationContext';
import { TrashIcon } from '@heroicons/react/24/outline';

interface AdminCollectionsResponse {
  collections: AdminCollectionInfo[];
  pagination: { page: number; limit: number; total: number; pages: number };
}

export default function AdminCollections() {
  const [collectionsData, setCollectionsData] = useState<AdminCollectionsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const { addNotification } = useNotification();
  const [deletingCollectionId, setDeletingCollectionId] = useState<string | null>(null);

  useEffect(() => {
    fetchAdminCollections(page);
  }, [page]);

  const fetchAdminCollections = async (currentPage: number) => {
    setIsLoading(true);
    setError('');
    try {
      const response = await fetch(`/api/admin/collections?page=${currentPage}&limit=25`, { credentials: 'include' });
      const data = await response.json();
      if (!response.ok) throw new Error((data as {error: string})?.error || 'Failed to fetch collections');
      setCollectionsData(data as AdminCollectionsResponse);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load collections');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteCollection = async (collectionId: string, collectionName: string) => {
      if (!window.confirm(`Are you sure you want to DELETE collection "${collectionName}" and all its games/players/scores? This is IRREVERSIBLE!`)) return;
      setDeletingCollectionId(collectionId);
      setError('');
      try {
        const response = await fetch(`/api/admin/collections/${collectionId}`, {
          method: 'DELETE',
          credentials: 'include',
        });
        const data = await response.json();
        if (!response.ok) throw new Error((data as {error: string})?.error || 'Failed to delete collection');
        addNotification('Collection deleted successfully!', 'success');
        fetchAdminCollections(1); // Refresh list, go to page 1
      } catch (err) {
         const errorMsg = err instanceof Error ? err.message : 'Could not delete collection';
         setError(errorMsg);
         addNotification(errorMsg, 'error');
      } finally {
         setDeletingCollectionId(null);
      }
  };
  
  const Pagination = () => {
      if (!collectionsData || collectionsData.pagination.pages <= 1) return null;
      const { page: currentPage, pages } = collectionsData.pagination;
      return (
        <div className="flex justify-center items-center space-x-2 mt-6">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="btn-secondary text-sm">Prev</button>
            <span>Page {currentPage} of {pages}</span>
            <button onClick={() => setPage(p => Math.min(pages, p + 1))} disabled={currentPage === pages} className="btn-secondary text-sm">Next</button>
        </div>
      );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Admin: All Collections</h1>
      
      {error && ( <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md dark:bg-red-900/20 dark:border-red-800 dark:text-red-400">{error}</div> )}

      <div className="shadow-md dark:shadow-none rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                 <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Name</th>
                 <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Owner</th>
                 <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Members</th>
                 <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Games</th>
                 <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Created</th>
                 <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {isLoading ? (
                 <tr><td colSpan={6} className="text-center p-4 dark:text-gray-400">Loading...</td></tr>
              ) : !collectionsData || collectionsData.collections.length === 0 ? (
                 <tr><td colSpan={6} className="text-center p-4 dark:text-gray-400">No collections found.</td></tr>
              ) : (
                 collectionsData.collections.map(coll => (
                  <tr key={coll.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">{coll.name}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{coll.owner_username}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-center text-gray-500 dark:text-gray-400">{coll.member_count}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-center text-gray-500 dark:text-gray-400">{coll.game_count}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{new Date(coll.created_at).toLocaleDateString()}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                       <button
                         onClick={() => handleDeleteCollection(coll.id, coll.name)}
                         disabled={deletingCollectionId === coll.id}
                         className="p-1 rounded text-red-600 hover:bg-red-100 dark:text-red-400 dark:hover:bg-red-900/50 disabled:opacity-50"
                         title="Delete Collection"
                       >
                          {deletingCollectionId === coll.id ? (
                             <svg className="animate-spin h-4 w-4" /* spinner */></svg>
                          ) : (
                             <TrashIcon className="h-4 w-4" />
                          )}
                       </button>
                    </td>
                  </tr>
                 ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      <Pagination />
    </div>
  );
} 